import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  BROWSER_TAB_POPOUT_LAUNCH_KEY,
  BROWSER_TAB_POPOUT_STORAGE_PREFIX,
  BrowserTabPopoutPayload,
} from '../models/browser-tab.models';
import { AuthSessionService } from './auth-session.service';
import { BrowserTabNavigationService } from './browser-tab-navigation.service';
import { BrowserTabService } from './browser-tab.service';

interface PopoutLaunchMarker {
  popoutId: string;
  routeUrl: string;
}

/** Strips query/hash and trailing slashes so opener and popout URLs compare reliably. */
export function normalizeRoutePath(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path || '/';
}

@Injectable({ providedIn: 'root' })
export class BrowserTabPopoutService {
  private readonly browserTabs = inject(BrowserTabService);
  private readonly tabNavigation = inject(BrowserTabNavigationService);

  /**
   * Moves the active tab into a new browser window and removes it from the opener.
   * Handoff uses localStorage only — the opened URL stays clean (no query params).
   */
  moveActiveTabToNewWindow(routeUrl: string, accessToken: string | null): void {
    const tabId = this.browserTabs.activeTabId();
    const tab = this.browserTabs.tabs().find((item) => item.id === tabId);
    if (!tab || !accessToken) {
      return;
    }

    const normalizedRouteUrl = normalizeRoutePath(routeUrl);
    const popoutId = crypto.randomUUID();
    const payload: BrowserTabPopoutPayload = {
      version: 2,
      tab: { id: tab.id, label: tab.label, tags: tab.tags, instanceId: tab.instanceId },
      pageState: this.browserTabs.getPageState(tabId),
      routeUrl: normalizedRouteUrl,
      accessToken,
    };

    localStorage.setItem(`${BROWSER_TAB_POPOUT_STORAGE_PREFIX}${popoutId}`, JSON.stringify(payload));
    localStorage.setItem(
      BROWSER_TAB_POPOUT_LAUNCH_KEY,
      JSON.stringify({ popoutId, routeUrl: normalizedRouteUrl } satisfies PopoutLaunchMarker),
    );

    // Avoid `noopener` / `noreferrer` feature flags — both make `window.open` return null
    // even on success, which aborted the handoff and cloned the parent session in the child.
    const opened = window.open(normalizedRouteUrl, '_blank');
    if (!opened) {
      localStorage.removeItem(BROWSER_TAB_POPOUT_LAUNCH_KEY);
      localStorage.removeItem(`${BROWSER_TAB_POPOUT_STORAGE_PREFIX}${popoutId}`);
      return;
    }

    try {
      opened.opener = null;
    } catch {
      // Cross-origin / browser policy — window still opened successfully.
    }

    this.browserTabs.beginPopoutTransfer(popoutId);

    if (this.browserTabs.tabs().length <= 1) {
      this.browserTabs.resetAfterLastTabPopout();
      void this.tabNavigation.navigateToTab('t1');
      return;
    }

    this.browserTabs.removeTab(tabId, { releaseRegistry: false });
    void this.tabNavigation.navigateToTab(this.browserTabs.activeTabId());
  }

  /** True while a popout handoff marker is waiting for the new window to consume. */
  hasPendingPopoutLaunch(): boolean {
    return localStorage.getItem(BROWSER_TAB_POPOUT_LAUNCH_KEY) !== null;
  }

  /** Route URL the pending popout window should load (path only). */
  getPendingPopoutRouteUrl(): string | null {
    const marker = this.readPendingLaunchMarker();
    return marker?.routeUrl ?? null;
  }

  /**
   * Hydrates a popout window when its URL matches a pending launch marker.
   * Returns null when this window is not the popout target.
   */
  tryHydrateFromPendingLaunch(auth: AuthSessionService, router: Router): string | null {
    const marker = this.readPendingLaunchMarker();
    if (!marker) {
      return null;
    }

    const targetPath = normalizeRoutePath(marker.routeUrl);
    const routerPath = normalizeRoutePath(router.url);
    const browserPath = normalizeRoutePath(window.location.pathname);
    const isPopoutTarget =
      routerPath === targetPath || (browserPath === targetPath && browserPath !== '/');

    if (!isPopoutTarget) {
      return null;
    }

    localStorage.removeItem(BROWSER_TAB_POPOUT_LAUNCH_KEY);

    const rawPayload = localStorage.getItem(`${BROWSER_TAB_POPOUT_STORAGE_PREFIX}${marker.popoutId}`);
    localStorage.removeItem(`${BROWSER_TAB_POPOUT_STORAGE_PREFIX}${marker.popoutId}`);

    if (!rawPayload) {
      return null;
    }

    let payload: BrowserTabPopoutPayload;
    try {
      payload = JSON.parse(rawPayload) as BrowserTabPopoutPayload;
    } catch {
      return null;
    }

    if (!payload.tab?.id || !payload.routeUrl || !payload.accessToken) {
      return null;
    }

    auth.establishSession(payload.accessToken);
    this.browserTabs.hydrateFromPopout(payload);
    return marker.routeUrl;
  }

  private readPendingLaunchMarker(): PopoutLaunchMarker | null {
    const rawMarker = localStorage.getItem(BROWSER_TAB_POPOUT_LAUNCH_KEY);
    if (!rawMarker) {
      return null;
    }

    try {
      return JSON.parse(rawMarker) as PopoutLaunchMarker;
    } catch {
      return null;
    }
  }
}
